import { Tool, QuestionnaireData, Recommendation } from '../types';
import { tools } from './tools';
import { filterTools } from './recommendations/filters';
import { scoreTools } from './recommendations/scoring';
import { applyDiversityFilter } from './recommendations/diversityFilter';
import { DEFAULT_RECOMMENDATIONS, MAX_RECOMMENDATIONS } from './recommendations/constants';
import { logger } from './logger';

export function getRecommendations(data: Partial<QuestionnaireData>): Recommendation[] {
  try {
    const normalizedData = {
      ...data,
      businessType: data.businessType || 'small-business',
      industry: data.industry || 'other',
      goals: data.goals || ['streamline-operations'],
      features: data.features || ['ease-of-use'],
      budget: data.budget || 'medium',
      teamSize: data.teamSize || 'solo',
      businessStage: data.businessStage || 'growing',
      technicalExpertise: data.technicalExpertise || 'intermediate',
    } as QuestionnaireData;

    const businessType = normalizedData.businessType;

    logger.info('Starting recommendations process', {
      totalTools: tools.length,
      businessType: normalizedData.businessType,
      industry: normalizedData.industry,
      features: normalizedData.features,
      goals: normalizedData.goals,
      budget: normalizedData.budget
    });

    const eligibleTools = filterTools(tools, normalizedData);

    logger.info('Filtered tools:', {
      eligible: eligibleTools.length,
      eligibleTools: eligibleTools.map(t => t.name),
      categories: [...new Set(eligibleTools.map(t => t.category))]
    });

    let workingTools = [...eligibleTools];

    if (workingTools.length < MAX_RECOMMENDATIONS) {
      logger.info('Adding more tools based on business type');
      const additionalTools = tools.filter(tool =>
        !workingTools.some(t => t.id === tool.id) && (
          tool.bestFor?.includes(businessType) ||
          tool.compatibleWith?.includes(normalizedData.businessSubType || '') ||
          tool.industries?.includes(normalizedData.industry)
        )
      );
      workingTools.push(...additionalTools);
    }

    if (workingTools.length < MAX_RECOMMENDATIONS) {
      logger.info('Adding tools from default categories');
      const defaultCategories = DEFAULT_RECOMMENDATIONS[businessType] || DEFAULT_RECOMMENDATIONS['small-business'];
      const defaultTools = tools.filter(tool =>
        !workingTools.some(t => t.id === tool.id) &&
        defaultCategories.includes(tool.category)
      );
      workingTools.push(...defaultTools);
    }

    const scoredTools = scoreTools(workingTools, normalizedData);

    logger.info('Scored tools:', {
      scored: scoredTools.length, 
      scores: scoredTools.map(t => ({ name: t.tool.name, score: t.score })),
      topScore: scoredTools[0]?.score,
      lowestScore: scoredTools[scoredTools.length - 1]?.score
    });
    
    // Apply diversity filter to ensure balanced recommendations
    let recommendations = applyDiversityFilter(
      scoredTools.filter(rec => rec.score > 0)
    );
    
    if (recommendations.length < MAX_RECOMMENDATIONS) {
      logger.info('Adding more recommendations to reach target');
      const remaining = scoredTools
        .filter(tool => tool.score > 0 && !recommendations.some(rec => rec.tool.id === tool.tool.id))
        .slice(0, MAX_RECOMMENDATIONS - recommendations.length);

      recommendations.push(...remaining);

      if (recommendations.length < MAX_RECOMMENDATIONS) {
        logger.info('Using default recommendations to fill remaining slots');
        const defaults = getDefaultRecommendations(businessType, tools)
          .filter(rec => !recommendations.some(r => r.tool.id === rec.tool.id))
          .slice(0, MAX_RECOMMENDATIONS - recommendations.length);

        recommendations.push(...defaults);
      }
    }

    recommendations = recommendations.slice(0, MAX_RECOMMENDATIONS);

    logger.info(`Generated ${recommendations.length} recommendations`);
    return recommendations;
  } catch (error) {
    logger.error('Error generating recommendations:', error);
    const defaults = getDefaultRecommendations(data.businessType || 'small-business', tools);
    return defaults.slice(0, MAX_RECOMMENDATIONS);
  }
}

function getDefaultRecommendations(businessType: string, allTools: Tool[]): Recommendation[] {
  // Default categories for each business type
  const defaultCategories: Record<string, string[]> = {
    solopreneur: ['Freelance Management', 'Project Management', 'Accounting', 'Marketing'],
    startup: ['Project Management', 'Sales & CRM', 'Analytics', 'Marketing'],
    'small-business': ['Accounting', 'Sales & CRM', 'Project Management', 'Marketing'],
    freelancer: ['Time Tracking', 'Freelance Management', 'Project Management', 'Marketing'],
    trades: ['Field Service', 'Scheduling', 'Payment Processing', 'Accounting'],
    'side-hustler': ['Marketing', 'E-commerce', 'Social Media Management', 'Time Tracking']
  };

  const categories = defaultCategories[businessType] || [];
  const recommendations: Recommendation[] = [];

  categories.forEach(category => {
    const tool = allTools.find(t => t.category === category);
    if (tool) {
      recommendations.push({
        tool,
        score: 70,
        matchScore: 65,
        reasons: [
          `Recommended for ${businessType}s`,
          `Essential ${category.toLowerCase()} tool`,
          'Matches your business needs'
        ]
      });
    }
  });

  return recommendations;
}